import { gql, useQuery } from "@apollo/client";
import { Avatar, Button, Icon, Link, TextField } from "@resource/atlas";
import { atlasGlobe, atlasImage } from "@resource/atlas/icons";
import { useFlags } from "@resource/client-ffs";
import { CustomerPlanEnum } from "@resource/common";
import { useAuthContext } from "auth/context";
import withPageAuthRequired from "auth/withPageAuthRequired";
import { useReplaceImageData } from "client/guide-content/rich-blocks/utils";
import ErrorPage from "components/Generic/ErrorPage";
import Layout from "components/Layout";
import Loading from "components/legacy/Loading/Loading";
import Brand from "components/legacy/Settings/Company/Brand/Brand";
import CustomLogistics from "components/legacy/Settings/Company/CustomLogistics/CustomLogistics";
import Images from "components/legacy/Settings/Company/Images/Images";
import Locations from "components/legacy/Settings/Company/Locations/Locations";
import Mission from "components/legacy/Settings/Company/Mission/Mission";
import Values from "components/legacy/Settings/Company/Values/Values";
import SettingsLayout from "components/legacy/Settings/SettingsLayout";
import { GuideContent } from "components/Settings/Company/GuideContent";
import FeatureFlags from "generated/FeatureFlags";
import {
  GlobalOrganizationContentForCompanySettings,
  UpdateCompanySettingsDefaultInterviewerImageBrandKit,
  UpdateCompanySettingsDefaultInterviewerImageBrandKitVariables,
  UpdateCompanySettingsLogoBrandKit,
  UpdateCompanySettingsLogoBrandKitVariables,
} from "generated/schemaTypes";
import { formatEntity } from "shared/constants/entities";
import { useInV2Frame } from "utils/in-iframe";
import { PermissionEnum } from "utils/permissions";
import useIntercom from "utils/useIntercom";
import useMutation from "utils/useMutation";

const UPDATE_ORG_LOGO = gql`
  mutation UpdateCompanySettingsLogoBrandKit(
    $input: UpdateOrganizationLogoInput!
  ) {
    updateOrganizationLogo(input: $input) {
      success
      code
      message
      organization {
        id
        companyLogoUrl
      }
    }
  }
`;

const UPDATE_ORG_DEFAULT_INTERVIEWER_IMAGE = gql`
  mutation UpdateCompanySettingsDefaultInterviewerImageBrandKit(
    $input: UpdateOrganizationInterviewerImageInput!
  ) {
    updateOrganizationInterviewerImage(input: $input) {
      success
      code
      message
      organization {
        id
        defaultAvatarImageUrl
      }
    }
  }
`;

const GLOBAL_ORGANIZATION_CONTENT_FOR_COMPANY_SETTINGS = gql`
  query GlobalOrganizationContentForCompanySettings {
    currentOrganization {
      id
      name
      locations(where: { isArchived: false }) {
        ...OrganizationLocation
      }
      missionStatement
      theme {
        ...OrganizationTheme
      }
      showTrim
      companyLogoUrl
      defaultAvatarImageUrl
      defaultGuideContent {
        id
        data
      }
      values {
        ...OrganizationValue
      }
      customLogisticGroups {
        ...OrganizationCustomLogisticGroup
      }
    }
  }
  ${Locations.fragments.location}
  ${CustomLogistics.fragments.customLogisticGroup}
  ${Values.fragments.organizationValue}
  ${Brand.fragments.theme}
`;

export function CompanyPage() {
  const isWithinV2 = useInV2Frame();
  const {
    data: companyData,
    loading: companyDataLoading,
    refetch,
  } = useQuery<GlobalOrganizationContentForCompanySettings>(
    GLOBAL_ORGANIZATION_CONTENT_FOR_COMPANY_SETTINGS
  );
  const { [FeatureFlags.ENABLE_SOURCING_ADDON]: sourcingAddonEnabled } =
    useFlags();
  const {
    zeusInfo: { isZeus, isZeusFeatureEnabled },
    checkRolePermissions,
  } = useAuthContext();
  const { callIntercom } = useIntercom();
  const isAuthorized = checkRolePermissions(
    PermissionEnum.ORGANIZATION_BRAND_WRITE
  );

  const [updateLogoUrl] = useMutation<
    UpdateCompanySettingsLogoBrandKit,
    UpdateCompanySettingsLogoBrandKitVariables
  >(UPDATE_ORG_LOGO);

  const [updateAvatarUrl] = useMutation<
    UpdateCompanySettingsDefaultInterviewerImageBrandKit,
    UpdateCompanySettingsDefaultInterviewerImageBrandKitVariables
  >(UPDATE_ORG_DEFAULT_INTERVIEWER_IMAGE);

  const { fileChooserInput, onReplaceImage } = useReplaceImageData();

  const shouldEnableLegacyOrgSettings = !isZeus || sourcingAddonEnabled;

  if (companyDataLoading) {
    return <Loading />;
  }

  const organization = companyData?.currentOrganization;
  if (!organization) {
    return <ErrorPage />;
  }

  const {
    companyLogoUrl,
    defaultAvatarImageUrl,
    locations,
    missionStatement,
    theme,
    values,
    customLogisticGroups,
    showTrim,
    name,
  } = organization;

  return (
    <>
      {isZeus ? (
        <div className="space-y-8">
          <div className="space-y-4">
            <h4 className="text-h4">Company Settings</h4>
            <div className="space-y-2 max-w-xs">
              <p className="text-body-md-heavy">Company name</p>
              <TextField isDisabled value={name} />
              <p className="text-body-sm text-subtle">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link
                  as="button"
                  onClick={() => {
                    callIntercom(
                      "showNewMessage",
                      "Can you help me update our organization name?"
                    );
                  }}
                >
                  Contact support
                </Link>{" "}
                to update your company name
              </p>
            </div>
          </div>
          <div className="h-[.0625rem] w-full bg-light-gray-500" />
          <div className="space-y-4">
            <h4 className="text-h4">Branding</h4>
            <div className="space-y-2">
              <p className="text-body-md-heavy">Company logo</p>
              <div className="flex items-center gap-4">
                <div className="min-w-[16rem] flex items-center justify-center h-20 rounded-sm border border-light-gray-500 py-4 px-8">
                  {companyLogoUrl ? (
                    <img src={companyLogoUrl} alt={name} className="h-full" />
                  ) : (
                    <Icon
                      content={atlasImage}
                      className="text-subtle w-6 h-6"
                    />
                  )}
                </div>
                <Button
                  onClick={() =>
                    onReplaceImage((url) =>
                      updateLogoUrl({
                        variables: {
                          input: {
                            companyLogoUrl: url,
                          },
                        },
                      })
                    )
                  }
                >
                  Upload logo
                </Button>
              </div>
              <p className="text-body-sm text-subtle">
                JPG, GIF or PNG. 5MB Max.
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-body-md-heavy">Company avatar</p>
              <div className="flex items-center gap-4">
                <Avatar
                  size="2xl"
                  image={defaultAvatarImageUrl ?? undefined}
                  variant="user"
                  className="flex-shrink-0"
                  disableTooltip
                />
                <Button
                  onClick={() =>
                    onReplaceImage((url) =>
                      updateAvatarUrl({
                        variables: {
                          input: {
                            defaultAvatarImageUrl: url,
                          },
                        },
                      })
                    )
                  }
                >
                  Upload avatar
                </Button>
              </div>
            </div>
            <p className="text-body-sm text-subtle">
              Use a square image for best results.
              <br />
              JPG, GIF or PNG. 5MB Max.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className=" text-body-lg text-subtle flex items-center">
            <Icon content={atlasGlobe} className="mr-1.5 w-6 h-6" />
            This carries through all {formatEntity("guide", { plural: true })},
            organization-wide
          </p>
          {shouldEnableLegacyOrgSettings && (
            <Locations
              locations={locations}
              refetchCompany={refetch}
              disabled={!isAuthorized}
            />
          )}
          <Images
            defaultAvatarImageUrl={defaultAvatarImageUrl}
            logoUrl={companyLogoUrl}
          />
          <Brand showTrim={showTrim} theme={theme} />
          {shouldEnableLegacyOrgSettings && (
            <Mission
              missionStatement={missionStatement}
              disabled={!isAuthorized}
            />
          )}
          {shouldEnableLegacyOrgSettings && (
            <Values values={values} disabled={!isAuthorized} />
          )}
          {shouldEnableLegacyOrgSettings && (
            <CustomLogistics
              customLogistics={customLogisticGroups}
              disabled={!isAuthorized}
            />
          )}
          {isZeusFeatureEnabled && (
            <>
              <hr className="mt-8 border-light-gray-600" />
              <GuideContent
                disabled={!isAuthorized}
                organizationName={organization.name}
                content={JSON.stringify(organization.defaultGuideContent?.data)}
                className="pb-10"
              />
            </>
          )}
        </div>
      )}
      {isWithinV2 && (
        <div className="space-y-4 mt-4">
          <Mission
            missionStatement={missionStatement}
            disabled={!isAuthorized}
          />
          <Values values={values} disabled={!isAuthorized} />
          <CustomLogistics
            customLogistics={customLogisticGroups}
            disabled={!isAuthorized}
          />
        </div>
      )}
      {fileChooserInput}
    </>
  );
}

export default withPageAuthRequired(CompanyPage, {
  requiredPermissions: (plan: CustomerPlanEnum) => {
    if (plan === CustomerPlanEnum.Platform) {
      return PermissionEnum.ORGANIZATION_WRITE;
    }
    // For self-serve plans, anyone can view the Company page
    return undefined;
  },
  getLayout: (page) => (
    <Layout>
      <SettingsLayout>{page}</SettingsLayout>
    </Layout>
  ),
});
